import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Button,
  Tooltip,
  Chip,
  Collapse,
  IconButton,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  DragIndicator as DragHandleIcon,
} from "@material-ui/icons";
import { useTranslation } from "../hooks/useTranslation";

const useStyles = makeStyles((theme) => ({
  nodeContainer: {
    marginBottom: theme.spacing(0.5),
    position: "relative",
    width: "fit-content",
    minWidth: "min-content",
    maxWidth: "max-content",
  },
  nodeContent: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 4,
    backgroundColor: "var(--ikaro-gray-light)",
    border: "1px solid var(--ikaro-gray-medium)",
    boxShadow: "var(--ikaro-shadow-light)",
    "&:hover": {
      backgroundColor: "var(--ikaro-gray-medium)",
      "& $actionButtons": {
        opacity: 1,
      },
      "& $dragHandle": {
        opacity: 0.7,
      },
    },
    transition: "background-color 0.2s",
    position: "relative",
    width: "fit-content",
    minWidth: "min-content",
    maxWidth: "max-content",
    flexWrap: "nowrap",
    cursor: "default",
  },
  nodeContentDragging: {
    opacity: 0.5,
    backgroundColor: "var(--ikaro-gray-medium)",
  },
  nodeContentDropTargetInside: {
    backgroundColor: "var(--ikaro-status-running-bg)",
    border: "2px solid var(--ikaro-status-running)",
    borderRadius: 4,
    boxShadow: "0 0 4px rgba(33, 150, 243, 0.3)",
  },
  nodeContentDropTargetBefore: {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: -4,
      left: 0,
      right: 0,
      height: 4,
      backgroundColor: "var(--ikaro-status-running)",
      zIndex: 5,
    },
  },
  nodeContentDropTargetAfter: {
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -4,
      left: 0,
      right: 0,
      height: 4,
      backgroundColor: "var(--ikaro-status-running)",
      zIndex: 5,
    },
  },
  dropArea: {
    position: "absolute",
    left: 0,
    right: 0,
    height: 6,
    backgroundColor: "transparent",
    zIndex: 100,
    "&:hover": {
      backgroundColor: "rgba(33, 150, 243, 0.1)",
    },
  },
  dropAreaBefore: {
    top: -3,
    cursor: "n-resize",
  },
  dropAreaAfter: {
    bottom: -3,
    cursor: "s-resize",
  },
  expandButton: {
    padding: 4,
    marginRight: theme.spacing(1),
  },
  nodeTitle: {
    flexGrow: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "text",
    maxWidth: "100%",
  },
  labelText: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    fontSize: "0.9em",
  },
  actionButtons: {
    display: "flex",
    opacity: 0.6,
    transition: "opacity 0.2s",
    flexShrink: 0,
    marginLeft: "auto",
  },
  nodeButton: {
    minWidth: 32,
    margin: theme.spacing(0, 0.5),
  },
  childrenContainer: {
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(0.5),
    borderLeft: `1px dashed ${theme.palette.grey[300]}`,
    marginLeft: theme.spacing(2),
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: theme.spacing(2),
      height: 1,
      borderTop: `1px dashed ${theme.palette.grey[300]}`,
    },
  },
  chip: {
    margin: 0,
    height: 22,
    fontSize: "0.7rem",
    flexShrink: 0,
    maxWidth: "100%",
    overflow: "hidden",
  },
  typeChip: {
    backgroundColor: "var(--ikaro-status-running-bg)",
  },
  labelChip: {
    backgroundColor: "var(--ikaro-status-success-bg)",
  },
  listChip: {
    backgroundColor: "var(--ikaro-purple-light)",
  },
  descriptionChip: {
    backgroundColor: "var(--ikaro-status-warning-bg)",
  },
  possibleValuesChip: {
    backgroundColor: "var(--ikaro-orange-light)",
  },
  indentGuide: {
    position: "absolute",
    left: (props) => -20 + props.level * 40,
    top: 0,
    bottom: 0,
    width: 20,
    borderLeft: (props) =>
      props.level > 0 ? `1px dashed ${theme.palette.grey[300]}` : "none",
  },
  childConnection: {
    position: "absolute",
    left: 0,
    top: -theme.spacing(0.5),
    width: theme.spacing(2),
    height: theme.spacing(0.5),
    borderLeft: `1px dashed ${theme.palette.grey[300]}`,
    borderBottom: `1px dashed ${theme.palette.grey[300]}`,
    borderBottomLeftRadius: 4,
  },
  dropIndicator: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    padding: "2px 8px",
    backgroundColor: "var(--ikaro-status-running)",
    color: "var(--ikaro-text-white)",
    borderRadius: 4,
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
    pointerEvents: "none",
    zIndex: 100,
    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
  },
  dropIndicatorBefore: {
    top: -15,
  },
  dropIndicatorInside: {
    top: "50%",
    transform: "translate(-50%, -50%)",
  },
  dropIndicatorAfter: {
    bottom: -15,
  },
  chipContainer: {
    display: "flex",
    flexWrap: "nowrap",
    gap: theme.spacing(0.5),
    marginRight: theme.spacing(1),
    flexGrow: 0,
    justifyContent: "flex-start",
    overflow: "hidden",
    flexShrink: 1,
    width: "fit-content",
  },
  dragHandle: {
    cursor: "grab",
    marginRight: theme.spacing(1),
    color: "var(--ikaro-gray-dark)",
    opacity: 0.3,
    transition: "opacity 0.2s",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      opacity: 1,
      color: "var(--ikaro-gray-darker)",
    },
    fontSize: "16px",
    flexShrink: 0,
  },
}));

// Função para mapear tipos para labels mais curtos
const getTypeLabel = (type, t) => {
  const typeLabels = {
    string: t("common:dataTypes.string"),
    int: t("common:dataTypes.int"),
    float: t("common:dataTypes.float"),
    date: t("common:dataTypes.date"),
    time: t("common:dataTypes.time"),
    date_time: t("common:dataTypes.dateTime"),
    bool: t("common:dataTypes.bool"),
    possible_values: t("common:dataTypes.possibleValues"),
  };

  return typeLabels[type] || type.toUpperCase();
};

const TreeNode = ({
  node,
  level,
  onAdd,
  onRemove,
  onEdit,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDraggable,
  dropPosition,
  children,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [showDropIndicator, setShowDropIndicator] = useState(false);
  const { t } = useTranslation("editor");

  const styleProps = { level };
  const classes = useStyles(styleProps);

  const handleToggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  const handleDragStart = (e) => {
    if (isDraggable) {
      setIsDragging(true);
      onDragStart(e);
    }
  };

  const handleDragEnd = (e) => {
    setIsDragging(false);
    setShowDropIndicator(false);
    onDragEnd(e);
  };

  const handleDragOver = (e) => {
    if (isDraggable) {
      e.preventDefault();
      setShowDropIndicator(true);
      onDragOver(e, "auto");
    }
  };

  const handleDragOverBefore = (e) => {
    if (isDraggable) {
      e.preventDefault();
      e.stopPropagation();
      setShowDropIndicator(true);
      onDragOver(e, "before");
    }
  };

  const handleDragOverAfter = (e) => {
    if (isDraggable) {
      e.preventDefault();
      e.stopPropagation();
      setShowDropIndicator(true);
      onDragOver(e, "after");
    }
  };

  const handleDragLeave = () => {
    setShowDropIndicator(false);
  };

  const handleDrop = (e) => {
    if (isDraggable) {
      setShowDropIndicator(false);
      onDrop(e);
    }
  };

  const getDropTargetClass = () => {
    if (!dropPosition) return "";

    switch (dropPosition) {
      case "inside":
        return classes.nodeContentDropTargetInside;
      case "before":
        return classes.nodeContentDropTargetBefore;
      case "after":
        return classes.nodeContentDropTargetAfter;
      default:
        return "";
    }
  };

  const getDropIndicatorText = () => {
    if (!dropPosition || !showDropIndicator) return null;

    let text;
    let className;

    switch (dropPosition) {
      case "inside":
        text = "Adicionar como filho";
        className = classes.dropIndicatorInside;
        break;
      case "before":
        text = "Inserir antes";
        className = classes.dropIndicatorBefore;
        break;
      case "after":
        text = "Inserir depois";
        className = classes.dropIndicatorAfter;
        break;
      default:
        return null;
    }

    return (
      <div className={`${classes.dropIndicator} ${className}`}>{text}</div>
    );
  };

  // Extrair os dados do nó, ignorando a variável description que não é usada
  // eslint-disable-next-line no-unused-vars
  const { name, label, type, list, description, possible_values } =
    node.nodeData || {};
  const isLeaf = node.nodeData?.isLeaf || false;
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className={classes.nodeContainer}>
      <div className={classes.indentGuide} />

      {/* Drop area for "before" position */}
      <div
        className={`${classes.dropArea} ${classes.dropAreaBefore}`}
        onDragOver={handleDragOverBefore}
        onDrop={handleDrop}
      />

      {showDropIndicator && getDropIndicatorText()}

      <Paper
        elevation={0}
        className={`${classes.nodeContent} 
          ${isDragging ? classes.nodeContentDragging : ""} 
          ${getDropTargetClass()}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {hasChildren && (
          <IconButton
            className={classes.expandButton}
            size="small"
            onClick={handleToggleExpand}
            style={{ flexShrink: 0 }}
          >
            {expanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </IconButton>
        )}

        <div
          className={classes.dragHandle}
          draggable={isDraggable}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <DragHandleIcon fontSize="small" />
        </div>

        <Typography
          variant="body2"
          className={classes.nodeTitle}
          title={name || "Sem nome"}
          noWrap
        >
          {name || "Sem nome"} {!isLeaf && list && "(Lista)"}
        </Typography>

        <div className={classes.chipContainer}>
          {isLeaf && (
            <>
              {type && (
                <Chip
                  label={getTypeLabel(type, t)}
                  size="small"
                  className={`${classes.chip} ${classes.typeChip}`}
                />
              )}

              {list && (
                <Chip
                  label={t("common:list")}
                  size="small"
                  className={`${classes.chip} ${classes.listChip}`}
                />
              )}

              {possible_values && possible_values.length > 0 && (
                <Tooltip title={possible_values.join(", ")}>
                  <Chip
                    label={`${possible_values.length} VALORES`}
                    size="small"
                    className={`${classes.chip} ${classes.possibleValuesChip}`}
                  />
                </Tooltip>
              )}
            </>
          )}

          {!isLeaf && list && (
            <Chip
              label={t("common:list")}
              size="small"
              className={`${classes.chip} ${classes.listChip}`}
            />
          )}
        </div>

        <div
          className={classes.actionButtons}
          style={{ display: "flex", flexShrink: 0 }}
        >
          {!isLeaf && (
            <Tooltip title={t("addChildItem")}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                className={classes.nodeButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onAdd();
                }}
                style={{
                  minWidth: "32px",
                  padding: "2px 6px",
                  backgroundColor: "var(--ikaro-bg-surface)",
                }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Tooltip>
          )}

          <Tooltip title="Editar item">
            <Button
              size="small"
              variant="outlined"
              color="primary"
              className={classes.nodeButton}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              style={{
                minWidth: "32px",
                padding: "2px 6px",
                backgroundColor: "var(--ikaro-bg-surface)",
              }}
            >
              <EditIcon fontSize="small" />
            </Button>
          </Tooltip>

          <Tooltip title="Remover item">
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              className={classes.nodeButton}
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              style={{
                minWidth: "32px",
                padding: "2px 6px",
                backgroundColor: "var(--ikaro-bg-surface)",
              }}
            >
              <DeleteIcon fontSize="small" />
            </Button>
          </Tooltip>
        </div>
      </Paper>

      {/* Drop area for "after" position */}
      <div
        className={`${classes.dropArea} ${classes.dropAreaAfter}`}
        onDragOver={handleDragOverAfter}
        onDrop={handleDrop}
      />

      {hasChildren && (
        <Collapse in={expanded} timeout="auto">
          <div className={classes.childrenContainer}>{children}</div>
        </Collapse>
      )}
    </div>
  );
};

export default TreeNode;
